import { getBackendUrl } from "@Utils";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { setShowNotification } from "../../actions/notification";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import IconComponent from "@CubeComponents/icons";
import axiosWrapper from "../../services/axiosWrapper";
import type { GetActionableCountResponse, ActionableCountRecord } from "@ServerTypes/projects/project.schema";
import { formCategories } from "@Helpers";
import { RootState } from "@Reducers/index";
import { useTranslation } from "react-i18next";

interface ActionCardsProps {
  onCountChange?: (count: number) => void;
}

function ActionCards({ onCountChange }: ActionCardsProps) {
  const [moduleCount, setModuleCount] = useState<ActionableCountRecord>({});
  const [actions, setActions] = useState<any>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { customModules, layout } = useSelector((state: RootState) => state);

  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const getModulesCount = async () => {
    try {
      const res = await axiosWrapper.post<GetActionableCountResponse>(`${getBackendUrl()}/projects/getActionableCount`);
      if (res.data.status) {
        setModuleCount(res.data.data ?? {});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getModulesCount();
  }, []);

  useEffect(() => {
    const modules = [
      {
        id: 1,
        count: moduleCount.reviews?.count || 0,
        label: "Reviews",
        icon: "Review",
        module: "reviews",
        allow: layout.WORKFLOW?.ENABLE,
        overDueCount: moduleCount.reviews?.overdueCount,
        next3DaysCount: moduleCount.reviews?.next3DaysCount,
        toolContent: `${moduleCount.reviews?.overdueCount || moduleCount.reviews?.next3DaysCount} Review(s) ${
          moduleCount.reviews?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
        }`,
      },
      {
        id: 2,
        count: moduleCount[formCategories.inspection]?.count || 0,
        label: "Inspections",
        icon: "inspectionForm",
        module: formCategories.inspection,
        allow: layout.INSPECTION?.ENABLE,
        overDueCount: moduleCount[formCategories.inspection]?.overdueCount,
        next3DaysCount: moduleCount[formCategories.inspection]?.next3DaysCount,
        toolContent: `${
          moduleCount[formCategories.inspection]?.overdueCount || moduleCount[formCategories.inspection]?.next3DaysCount
        } Inspections ${
          moduleCount[formCategories.inspection]?.overdueCount
            ? "exceeded due date"
            : "approaching due date in 3 day(s)"
        }`,
      },
      {
        id: 3,
        count: moduleCount[formCategories.progressUpdates]?.count || 0,
        label: "Progress updates",
        icon: "ProgressUpdates",
        module: formCategories.progressUpdates,
        allow: layout.PROGRESS_UPDATES?.ENABLE,
        overDueCount: moduleCount[formCategories.progressUpdates]?.overdueCount,
        next3DaysCount: moduleCount[formCategories.progressUpdates]?.next3DaysCount,
        toolContent: `${
          moduleCount[formCategories.progressUpdates]?.overdueCount ||
          moduleCount[formCategories.progressUpdates]?.next3DaysCount
        } Progress updates ${
          moduleCount[formCategories.progressUpdates]?.overdueCount
            ? "exceeded due date"
            : "approaching due date in 3 day(s)"
        }`,
      },
    ];
    customModules?.map((module: any, index: any) => {
      modules.push({
        id: index + 6,
        count: moduleCount[module.resourceType]?.count || 0,
        label: module.name,
        icon: module.icon,
        module: module.resourceType,
        allow: true,
        overDueCount: moduleCount[module.resourceType]?.overdueCount,
        next3DaysCount: moduleCount[module.resourceType]?.next3DaysCount,
        toolContent: `${
          moduleCount[module.resourceType]?.overdueCount || moduleCount[module.resourceType]?.next3DaysCount
        } ${module.name} ${
          moduleCount[module.resourceType]?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
        }`,
      });
    });
    setActions(modules);
  }, [customModules, moduleCount]);

  useEffect(() => {
    let total = 0;
    actions.map((module: any) => {
      if (module.allow) {
        total += module.count;
      }
    });
    setTotalCount(total);
    if (!loading) {
      onCountChange?.(total);
    }
  }, [actions, onCountChange, loading]);

  const dispatch = useDispatch();
  const closeNotificationModal = () => {
    dispatch(setShowNotification(false));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <IconComponent name="loader-icon" className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full max-w-3xl p-3">
        <div className="ps-8 py-4 font-semibold">
          {t("Take Action")}{" "}
          <span className="inline-flex items-center p-1 ms-2 text-sm font-bold bg-[#ECF3FE] rounded">{totalCount}</span>
        </div>
        <hr className="my-2 border-blue-50" />
      </div>
      <div className="flex flex-wrap gap-3 pe-6 ps-8 w-full">
        {actions.map(
          (action: any) =>
            action.allow &&
            action.count > 0 && (
              <Link
                to={`/notifications?section=takeAction&module=${action.module}`}
                onClick={closeNotificationModal}
                className="no-underline text-inherit"
                key={action.id}
              >
                <div className="flex items-center justify-between rounded-lg p-4 w-full border cursor-pointer">
                  <div className="flex flex-wrap gap-3 items-center min-w-0">
                    <span>
                      <IconComponent name={action.icon} />
                    </span>
                    <span className="text-xl font-bold">{action.count}</span>
                    <span className="text-base sm:text-lg font-semibold whitespace-nowrap">{action.label}</span>
                    {(action.overDueCount > 0 || action.next3DaysCount > 0) && (
                      <ToolTipComponent
                        position="bottom"
                        content={action.toolContent}
                        className="max-w-[12.5rem] break-words"
                      >
                        <span
                          className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default ${
                            action.overDueCount ? "bg-red-500" : "bg-yellow-400"
                          }`}
                        >
                          !
                        </span>
                      </ToolTipComponent>
                    )}
                  </div>
                </div>
              </Link>
            ),
        )}
      </div>
    </div>
  );
}

export default ActionCards;
